import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useIsAdmin } from './useAdmin';
import { applyBrandTheme, clearBrandTheme } from '@/lib/brand-theme';
import { FRANKLY_BRAND_SLUG } from '@/lib/platform-modules';
import { isPlatformDomain } from '@/lib/platform-domains';

export interface Brand {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  email_domain: string | null;
  custom_domain: string | null;
  domain_status: string | null;
  settings: Record<string, unknown> | null;
  features: Record<string, boolean> | null;
}

export type BrandRole = 'member' | 'brand_admin';

export interface UserBrandWithRole {
  brand: Brand;
  role: BrandRole;
}

interface BrandContextType {
  /** The platform brand's id (Frankly). Null only while loading or if the row is missing. */
  activeBrandId: string | null;
  /** All brands the current user has access to (admin: every brand) — admin surfaces only */
  brands: Brand[];
  /** User's brands with their role info */
  userBrandsWithRoles: UserBrandWithRole[];
  /** Whether brand data is still loading — gates FeatureRoute, so it must stay true until the platform row resolves */
  isLoading: boolean;
  /** The platform brand row (Frankly) — the single identity since #2909 */
  activeBrand: Brand | null;
  /** True when user is on a custom domain but doesn't have access to that brand */
  isDomainMismatch: boolean;
  /** Whether current user is a brand admin for the platform brand */
  isBrandAdmin: boolean;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

/** Check if the current hostname is a known brand custom domain */
function detectDomainBrand(brands: Brand[]): Brand | null {
  const hostname = window.location.hostname;
  if (isPlatformDomain(hostname)) return null;
  return brands.find(b => b.custom_domain === hostname && b.domain_status === 'active') ?? null;
}

const BRAND_COLUMNS = 'id, name, slug, logo_url, email_domain, custom_domain, domain_status, settings, features';

export function BrandProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { data: isAdmin } = useIsAdmin();

  // The platform brand (Frankly) IS the active brand for everyone since #2909.
  //
  // Fetched directly rather than derived from the user's memberships: brokers
  // (John) are not members of the Frankly brand, but RLS lets any
  // authenticated user read the brands table, and feature flags / identity
  // must resolve for them too. Brand selection, the switcher, and the
  // "All Brands" null state are gone — assignment (RLS) is the access
  // boundary, and brand_id is inert attribution.
  const { data: platformBrand = null, isLoading: isLoadingPlatform } = useQuery({
    queryKey: ['platform-brand'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brands')
        .select(BRAND_COLUMNS)
        .eq('slug', FRANKLY_BRAND_SLUG)
        .maybeSingle();
      // THROW, don't swallow: a returned null would be cached as fresh success
      // for 30 minutes with no retry, flags would fall back to DEFAULT_FEATURES,
      // and FeatureRoute would one-shot-bounce /cold-replies to /dashboard.
      // Throwing keeps isLoading true through React Query's retries instead.
      if (error) throw error;
      return data as Brand | null;
    },
    enabled: !!user?.id,
    staleTime: 30 * 60_000,
  });

  // Fetch user's brands via user_brands join (including role)
  // NOTE: Only select columns that exist on the production brands table.
  // New columns (resend_domain_id, sending_domain_status, etc.) are fetched
  // separately by the components that need them.
  const { data: userBrandsRaw = [], isLoading } = useQuery({
    queryKey: ['user-brands', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('user_brands')
        .select(`brand_id, is_default, role, brands(${BRAND_COLUMNS})`)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error fetching user brands:', error);
        return [];
      }

      return (data ?? [])
        .filter((ub: unknown) => (ub as { brands: Brand | null }).brands)
        .map((ub: unknown) => ({
          brand: (ub as { brands: Brand }).brands,
          role: ((ub as { role?: string }).role || 'member') as BrandRole,
        }));
    },
    enabled: !!user?.id,
    staleTime: 5 * 60_000,
  });

  // Agency admins: fetch ALL brands for the admin management surfaces
  const { data: allBrandsRaw = [], isLoading: isLoadingAllBrands } = useQuery({
    queryKey: ['all-brands-for-admin'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brands')
        .select(BRAND_COLUMNS)
        .order('name');
      if (error) {
        console.error('Error fetching all brands:', error);
        return [];
      }
      return data as Brand[];
    },
    enabled: isAdmin === true,
    staleTime: 5 * 60_000,
  });

  // For admins, merge all brands with any explicit role assignments
  const userBrandsWithRoles: UserBrandWithRole[] = isAdmin === true
    ? allBrandsRaw.map((brand) => {
        const explicit = userBrandsRaw.find((ubr: UserBrandWithRole) => ubr.brand.id === brand.id);
        return { brand, role: explicit?.role ?? ('member' as BrandRole) };
      })
    : userBrandsRaw;
  const brands: Brand[] = userBrandsWithRoles.map((ubr) => ubr.brand);

  // Domain mismatch detection stays alive for DomainGate until app.dellarok.com
  // is retired (#2885): a visitor on a custom domain they can't access is
  // still walled.
  const domainBrand = brands.length > 0 ? detectDomainBrand(brands) : null;
  const isCustomDomain = !isPlatformDomain(window.location.hostname);
  const isDomainMismatch = isCustomDomain && brands.length > 0 && !domainBrand;

  const activeBrand = platformBrand;
  const activeBrandId = platformBrand?.id ?? null;
  const isBrandAdmin = activeBrandId
    ? userBrandsWithRoles.some(ubr => ubr.brand.id === activeBrandId && ubr.role === 'brand_admin')
    : false;

  // Apply brand accent color to CSS custom properties
  const accentColor = (activeBrand?.settings as Record<string, unknown> | null)?.accent_color as string | undefined;
  useEffect(() => {
    if (accentColor) {
      applyBrandTheme(accentColor);
    } else {
      clearBrandTheme();
    }
    return () => clearBrandTheme();
  }, [accentColor]);

  return (
    <BrandContext.Provider
      value={{
        activeBrandId,
        brands,
        userBrandsWithRoles,
        isLoading: (isAdmin === true ? (isLoading || isLoadingAllBrands) : isLoading) || isLoadingPlatform,
        activeBrand,
        isDomainMismatch,
        isBrandAdmin,
      }}
    >
      {children}
    </BrandContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBrand() {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used within a BrandProvider');
  return ctx;
}

/**
 * Formerly added a `brand_id` filter to a Supabase query. Now a no-op (#2862).
 *
 * Brand filtering is retired. `brand_id` was never an access boundary — per
 * `.claude/rules/supabase.md` it is management attribution, and the database
 * enforces who-sees-what through `is_assigned_to_account` and the
 * `restrict_to_assigned` flag. This helper was a client-side convenience filter
 * layered on top, and by the end it was doing real harm in three ways:
 *
 *   * It was load-bearing by accident. Until the Stage 1 and Stage 2 locks
 *     landed, RLS let the unrestricted users reach all 478 accounts and this
 *     filter was the only thing holding them to 402 — a boundary enforced in the
 *     browser, which is exactly what this epic exists to remove.
 *   * `.eq()` excludes NULLs, and `brand_id` is null on 38-75% of rows across
 *     seven tables, so it was silently hiding data nobody meant to hide.
 *   * It produced a null state (no active brand) that broke screens outright —
 *     see #2872, where it caused a contract template to be overwritten with
 *     empty content.
 *
 * Kept as an identity function rather than deleted from ~30 call sites, so the
 * change is one line to revert if anything regresses. The call sites come out in
 * a follow-up cleanup once this has proven itself.
 *
 * Post-merge effect, measured: John 402 -> 24 and frank.carrera 402 -> 156 (both
 * narrowed by RLS, which is the point), Antonio 402 -> 478 (intended — Frankly is
 * not a team and he sees everything).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function scopeByBrand(
  query: unknown,
  _brandId: string | null,
  _column = 'brand_id',
): unknown {
  return query;
}
